// Client Portal — Facility user management (shared by Admin, PAS, Aftercare).
import { useState, useEffect, useCallback } from "react";
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  Info,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  Send,
  Stethoscope,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Upload,
  UserPlus,
  X,
} from "lucide-react";
import { authFetch } from "../lib/api";
import { describeParse, parseRosterCsv } from "../lib/rosterCsv";

// Where an invited user should land after setting their password. Must stay
// in step with ALLOWED_RESET_ORIGINS in the auth service: an origin it does
// not recognise is silently swapped for APP_URL, so a typo here sends people
// to the wrong app with no error.
const INVITE_DESTINATIONS = [
  {
    id: "https://fieldiq.hyveappliedintelligence.com",
    label: "FieldIQ (field app)",
  },
  { id: "https://app.hyveappliedintelligence.com", label: "Client Portal" },
  { id: "https://buildiq.hyveappliedintelligence.com", label: "BuildIQ" },
  // Deliberately NOT an option: the admin console is platform staff only, and
  // an invited facility user cannot use it.
];

// Ambiguous glyphs are omitted deliberately: this password gets read down a
// phone line or copied off a screen, where 0/O and 1/l/I are what turn a
// working credential into a failed sign-in.
const TEMP_PW_ALPHABET =
  "ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";

const generateTempPassword = (length = 16) => {
  const bytes = new Uint32Array(length);
  // crypto, not Math.random — this is a live credential until its holder
  // replaces it.
  window.crypto.getRandomValues(bytes);
  return Array.from(
    bytes,
    (b) => TEMP_PW_ALPHABET[b % TEMP_PW_ALPHABET.length],
  ).join("");
};

const IMPORT_STATUS_STYLE = {
  created: "text-green-700 bg-green-50 border-green-200",
  existing: "text-slate-600 bg-slate-50 border-slate-200",
  error: "text-red-700 bg-red-50 border-red-200",
};

export const UserManagement = ({ facility }) => {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    role: "user",
  });
  const [inviting, setInviting] = useState(false);
  // Roster import runs in two phases so the whole list can be checked before
  // anyone is emailed: import grants access, then invites go out separately.
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendingTo, setSendingTo] = useState(null);
  // Default to where the admin already is, which is what this used to send
  // unconditionally — so nothing changes until someone picks a destination.
  // Defaults to FieldIQ, not to whichever console this is: a roster import is
  // overwhelmingly field staff, and the destination should follow the user
  // being invited rather than the admin doing the inviting.
  const [destination, setDestination] = useState(INVITE_DESTINATIONS[0].id);
  const [search, setSearch] = useState("");
  const [inviteFilter, setInviteFilter] = useState("all");
  // Separate from inviteFilter so the two compose. "No link sent yet" plus
  // "never" is the list of people who still need a link; "signed_in" is the
  // inverse view — who is actually using the thing. Was a Hide-signed-in
  // checkbox; a second checkbox for the inverse could be ticked alongside it
  // and match nobody, so the two states share one control.
  const [signInFilter, setSignInFilter] = useState("all");
  // Access to THIS facility (usa.is_active), matching the Status badge and the
  // toggle on each row. It used to read saasframework.users.is_active instead,
  // while the button beside it changed facility access — so disabling someone
  // and then filtering to "Disabled" found nothing, which is a large part of
  // why disable read as deletion. The account-wide flag is still selectable,
  // as "Account disabled platform-wide".
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [sendSummary, setSendSummary] = useState(null);
  const fid = facility.facility_id;

  const parsedRows = csvText ? parseRosterCsv(csvText) : [];
  const parseInfo = csvText ? describeParse(csvText) : null;
  const validRows = parsedRows.filter((r) => r.valid);
  const importedIds = (importResults?.results || [])
    .filter((r) => r.user_id)
    .map((r) => r.user_id);

  const runImport = async () => {
    setImporting(true);
    setSendSummary(null);
    const res = await authFetch("/facility-users/bulk-import", {
      method: "POST",
      body: JSON.stringify({
        facility_id: fid,
        rows: validRows.map(({ email, first, last, role }) => ({
          email,
          first,
          last,
          role,
        })),
      }),
    });
    setImporting(false);
    if (res?.error) {
      setImportResults({ results: [], summary: null, error: res.error });
      return;
    }
    setImportResults(res);
    fetchUsers();
  };

  const sendInvites = async () => {
    setSending(true);
    const res = await authFetch("/facility-users/bulk-send-invites", {
      method: "POST",
      body: JSON.stringify({
        facility_id: fid,
        user_ids: importedIds,
        app_url: destination,
      }),
    });
    setSending(false);
    setSendSummary(res?.error ? { error: res.error } : res.summary);
  };

  // The bulk send above is only reachable while the import panel is open —
  // close it (or refresh) and those users have access but have never been
  // emailed, with no way to reach them. This is that way. Same endpoint, one
  // id: it already verifies the user holds active access to THIS facility.
  // Access diagnostics for one user. Scope is deliberate: every per-user
  // table is auth state, so this answers "why can they not get in" and makes
  // no claim about how the product is behaving for them — conversation_logs
  // and feedback key on session_id, with no user to join to.
  const [diagFor, setDiagFor] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError] = useState("");

  const runDiagnostics = async (user) => {
    setDiagFor(user);
    setDiag(null);
    setDiagError("");
    setDiagLoading(true);
    try {
      const res = await authFetch(
        `/facility-users/${user.id}/diagnostics?facility_id=${encodeURIComponent(fid)}`,
      );
      if (res?.error) setDiagError(res.error);
      else setDiag(res);
    } catch {
      setDiagError("Could not reach the server.");
    }
    setDiagLoading(false);
  };

  // Set a password directly, for people email cannot reach — no address, mail
  // bouncing, or standing in front of you. The link remains the better path
  // whenever mail works, so this sits behind its own action.
  const [tempPwFor, setTempPwFor] = useState(null);
  const [tempPw, setTempPw] = useState("");
  const [tempPwSaving, setTempPwSaving] = useState(false);
  const [tempPwMsg, setTempPwMsg] = useState(null);

  const openTempPassword = (user) => {
    setTempPwFor(user);
    setTempPw(generateTempPassword());
    setTempPwMsg(null);
  };

  const submitTempPassword = async () => {
    if (tempPw.length < 12) {
      setTempPwMsg({ err: true, text: "Use at least 12 characters." });
      return;
    }
    setTempPwSaving(true);
    setTempPwMsg(null);
    const res = await authFetch(
      `/facility-users/${tempPwFor.id}/temp-password?facility_id=${encodeURIComponent(fid)}`,
      { method: "POST", body: JSON.stringify({ password: tempPw }) },
    );
    setTempPwSaving(false);
    if (res?.error || !res?.success) {
      let text = res?.error || "Could not set the password.";
      try {
        text = JSON.parse(text).error || text;
      } catch {
        /* not JSON — show as-is */
      }
      setTempPwMsg({ err: true, text });
      return;
    }
    setTempPwMsg({
      err: false,
      text: "Password set. Give it to them now — it is not shown again.",
    });
    fetchUsers();
  };

  const sendSetPasswordLink = async (userId, email) => {
    setSendingTo(userId);
    let res;
    try {
      res = await authFetch("/facility-users/bulk-send-invites", {
        method: "POST",
        body: JSON.stringify({
          facility_id: fid,
          user_ids: [userId],
          app_url: destination,
        }),
      });
    } catch (err) {
      setSendingTo(null);
      window.alert(`Could not reach the server to email ${email}: ${err}`);
      return;
    }
    setSendingTo(null);
    const row = res?.results?.[0];
    if (res?.error || !row?.sent) {
      // The server now says WHY (quota vs bad address vs credentials), so
      // lead with that instead of sending the admin to the service log.
      const reason = res?.error || row?.detail;
      window.alert(
        reason
          ? `Could not email ${email} — ${reason}.`
          : `Could not email ${email}. The mail provider gave no reason; ` +
              `the auth service log will have the SMTP error.`,
      );
      return;
    }
    window.alert(
      `Set-password link sent to ${email}. It is good for ` +
        `${res.summary?.expires_in_days ?? "a few"} days.`,
    );
  };

  const resetImport = () => {
    setCsvText("");
    setImportResults(null);
    setSendSummary(null);
    setShowImport(false);
  };

  // Filters. "Email sent" comes from invite_sent_at, which the API reads from
  // facility_user_invite_sends (migration 154) — one row per link that
  // actually left the building, written only after the send succeeds. It is
  // NOT derived from password_reset_tokens, which also covers self-service
  // resets and once counted failed sends as delivered. Anything sent before
  // 154 was applied has no row, so it reads as never sent.
  //
  // A signed-in user demonstrably received a link, whenever it went out — so
  // hiding them is the one reliable way to narrow a pre-154 roster down to
  // people who may still be waiting on one.
  // "Active and signed in" subsumes the sign-in filter. Deriving the effective
  // value here — rather than only greying the control — is what stops a
  // leftover "Never signed in" from ANDing with it and matching nobody.
  const effectiveSignIn =
    statusFilter === "active_signed_in" ? "signed_in" : signInFilter;

  const visibleUsers = users.filter((u) => {
    const q = search.trim().toLowerCase();
    if (q) {
      const haystack = [u.first_name, u.last_name, u.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (inviteFilter === "sent" && !u.invite_sent_at) return false;
    if (inviteFilter === "not_sent" && u.invite_sent_at) return false;
    if (effectiveSignIn === "never" && u.last_login_at) return false;
    if (effectiveSignIn === "signed_in" && !u.last_login_at) return false;
    // Access to this facility, matching the badge and the toggle. "Disabled"
    // used to read the account-wide flag, so it never found a user disabled
    // by the button next to it.
    const hasAccess = u.access_is_active !== false;
    if (statusFilter === "active" && !(hasAccess && u.is_active)) return false;
    if (statusFilter === "disabled" && hasAccess) return false;
    if (statusFilter === "account_disabled" && u.is_active) return false;
    // Signed in NOW — a live session row — not last_login_at's "ever".
    if (
      statusFilter === "active_signed_in" &&
      !(hasAccess && u.is_active && u.signed_in_now)
    )
      return false;
    return true;
  });

  // Sort keys per column. Nulls collapse to "" / 0 so a direction still
  // applies to them instead of leaving them stranded wherever they were.
  const SORT_VALUE = {
    name: (u) =>
      `${u.first_name || ""} ${u.last_name || ""}`.trim().toLowerCase() ||
      (u.email || "").toLowerCase(),
    role: (u) => (u.client_role || "user").toLowerCase(),
    status: (u) => (u.is_active ? 1 : 0),
    invite: (u) => (u.invite_sent_at ? Date.parse(u.invite_sent_at) : 0),
    // Never-signed-in sorts as 0, so ascending puts the people who have never
    // been in at the top — which is the reason to sort by this at all.
    last_login: (u) => (u.last_login_at ? Date.parse(u.last_login_at) : 0),
  };

  const sortedUsers = [...visibleUsers].sort((a, b) => {
    const get = SORT_VALUE[sortField] || SORT_VALUE.name;
    const va = get(a);
    const vb = get(b);
    let cmp;
    if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
    else
      cmp = String(va).localeCompare(String(vb), undefined, {
        numeric: true,
      });
    // Email as the tiebreak so equal keys keep a stable, predictable order
    // rather than shuffling between renders.
    if (cmp === 0) cmp = (a.email || "").localeCompare(b.email || "");
    return sortAsc ? cmp : -cmp;
  });

  const totalPages = pageSize
    ? Math.max(1, Math.ceil(sortedUsers.length / pageSize))
    : 1;
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = pageSize
    ? sortedUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : sortedUsers;

  // Any change to what is being shown sends you back to the first page —
  // landing on page 4 of a 1-page result reads as "no users".
  useEffect(() => {
    setPage(1);
  }, [search, inviteFilter, signInFilter, statusFilter, pageSize]);

  const toggleSort = (field) => {
    if (sortField === field) setSortAsc((a) => !a);
    else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  // Sortable column header. Services and Actions are not sortable — there is
  // no single value to order them by.
  const SortHeader = ({ field, label }) => (
    <th
      onClick={() => toggleSort(field)}
      className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700"
    >
      {label}
      {sortField === field &&
        (sortAsc ? (
          <ChevronUp size={12} className="inline ml-1" />
        ) : (
          <ChevronDown size={12} className="inline ml-1" />
        ))}
    </th>
  );

  const toggleSelected = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Scoped to the page, not the whole filtered set: a header checkbox that
  // silently selected 300 people across 12 pages would be a footgun on a
  // button that emails them all. Selection itself survives paging — sending
  // still operates on every selected user in the filter, not just this page.
  const allVisibleSelected =
    pagedUsers.length > 0 && pagedUsers.every((u) => selected.has(u.id));

  const toggleSelectAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) pagedUsers.forEach((u) => next.delete(u.id));
      else pagedUsers.forEach((u) => next.add(u.id));
      return next;
    });

  // Bulk (re)send to the current selection. Same endpoint as the per-user
  // button and the post-import send — it verifies facility access per id.
  // Effective selection = selected AND currently visible. Selecting rows and
  // then changing the search/filter empties this while the bar still shows a
  // selection, which is how a click could silently do nothing.
  const selectedVisible = visibleUsers.filter((u) => selected.has(u.id));

  const sendSelected = async () => {
    const ids = selectedVisible.map((u) => u.id);
    if (!ids.length) {
      window.alert(
        "Nothing to send: the users you selected are not in the current " +
          "search or filter. Clear the filter, or select someone shown here.",
      );
      return;
    }
    setBulkSending(true);
    setBulkResult(null);
    let res;
    try {
      res = await authFetch("/facility-users/bulk-send-invites", {
        method: "POST",
        body: JSON.stringify({
          facility_id: fid,
          user_ids: ids,
          app_url: destination,
        }),
      });
    } catch (err) {
      setBulkSending(false);
      setBulkResult({ error: `Could not reach the server: ${err}` });
      return;
    }
    setBulkSending(false);
    if (res?.error) {
      setBulkResult({ error: res.error });
      return;
    }
    setBulkResult({ ...res.summary, results: res.results });
    // Clear only the ones that actually went, so a retry re-sends the rest.
    const succeeded = new Set(
      (res.results || []).filter((r) => r.sent).map((r) => r.user_id),
    );
    setSelected(
      (prev) => new Set([...prev].filter((id) => !succeeded.has(id))),
    );
    fetchUsers();
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const [data, pending] = await Promise.all([
      authFetch(
        // Disabled users included: dropping them from the grid is what
        // made disabling look like deletion. They render greyed with a
        // way back.
        `/facility-users?facility_id=${encodeURIComponent(fid)}&include_disabled=true`,
      ),
      authFetch(
        `/pending-registrations?facility_id=${encodeURIComponent(fid)}`,
      ),
    ]);
    if (data.users) setUsers(data.users);
    if (pending.users) setPendingUsers(pending.users);
    setLoading(false);
  }, [fid]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const approveUser = async (userId) => {
    await authFetch("/facility-users/toggle", {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        facility_id: fid,
        is_active: true,
      }),
    });
    fetchUsers();
  };

  // Its own endpoint, not toggle(is_active:false). "Pending" IS is_active =
  // false, so the old call wrote the state it was trying to leave and the row
  // stayed in the queue forever.
  const rejectUser = async (userId, email) => {
    if (
      !window.confirm(
        `Reject the access request from ${email}? They stay on record as ` +
          `rejected and drop off this list. Approving them later is still ` +
          `possible.`,
      )
    )
      return;
    let res;
    try {
      res = await authFetch("/facility-users/reject", {
        method: "POST",
        body: JSON.stringify({ user_id: userId, facility_id: fid }),
      });
    } catch (err) {
      window.alert(`Could not reach the server to reject ${email}: ${err}`);
      return;
    }
    if (res?.error) {
      window.alert(`Could not reject ${email} — ${res.error}`);
      return;
    }
    fetchUsers();
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!invite.email || invite.password.length < 8) return;
    setInviting(true);
    await authFetch("/facility-users/invite", {
      method: "POST",
      body: JSON.stringify({
        facility_id: fid,
        ...invite,
        app_url: destination,
      }),
    });
    setInviting(false);
    setShowInvite(false);
    setInvite({
      email: "",
      first_name: "",
      last_name: "",
      password: "",
      role: "user",
    });
    fetchUsers();
  };

  // Disable is non-destructive and always has been: the endpoint UPDATEs the
  // row. Erasing a membership is deleteUserAccess below, which is a different
  // endpoint, a different button and a different confirm.
  const toggleUser = async (userId, isActive) => {
    const res = await authFetch("/facility-users/toggle", {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        facility_id: fid,
        is_active: !isActive,
      }),
    });
    // A 404 here means the membership row is already gone — worth saying,
    // because it previously came back as a silent success and read as
    // "disable removed them".
    if (res?.error) alert(res.error);
    fetchUsers();
  };

  const deleteUserAccess = async () => {
    const u = confirmDeleteUser;
    if (!u) return;
    setDeletingUser(true);
    const res = await authFetch("/facility-users/delete-access", {
      method: "POST",
      body: JSON.stringify({ user_id: u.id, facility_id: fid }),
    });
    setDeletingUser(false);
    setConfirmDeleteUser(null);
    if (res?.error) alert(res.error);
    fetchUsers();
  };

  const setRole = async (userId, role) => {
    await authFetch("/facility-users/set-role", {
      method: "POST",
      body: JSON.stringify({ user_id: userId, facility_id: fid, role }),
    });
    fetchUsers();
  };

  const setServiceAccess = async (
    userId,
    serviceType,
    accessRole,
    revoke = false,
  ) => {
    await authFetch("/facility-users/set-service-access", {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        facility_id: facility.facility_id,
        service_type: serviceType,
        access_role: accessRole,
        revoke,
      }),
    });
    fetchUsers();
  };

  return (
    <div className="space-y-4">
      {showImport && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              <Upload size={15} className="text-blue-500" />
              Import roster
            </h3>
            <button
              onClick={resetImport}
              className="text-slate-400 hover:text-slate-600"
            >
              <X size={16} />
            </button>
          </div>

          {!importResults && (
            <>
              <p className="text-xs text-slate-500">
                One user per line:{" "}
                <code className="bg-slate-100 px-1 rounded">
                  email, first, last, role
                </code>
                . Imported users are active immediately — they never appear in
                Pending Registrations, they just choose a password.
              </p>
              <textarea
                rows={6}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder={
                  "email, first, last, role\npat@example.com, Pat, Casey, editor"
                }
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              <div className="flex items-center gap-3">
                <label className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer">
                  Upload a .csv
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const reader = new FileReader();
                      reader.onload = (ev) =>
                        setCsvText(String(ev.target.result || ""));
                      reader.readAsText(f);
                    }}
                  />
                </label>
                {parsedRows.length > 0 && (
                  <span className="text-xs text-slate-500">
                    {validRows.length} ready
                    {parsedRows.length !== validRows.length && (
                      <span className="text-red-600">
                        {" · "}
                        {parsedRows.length - validRows.length} unusable
                      </span>
                    )}
                    {parseInfo && (
                      <span className="text-slate-400">
                        {" · read as "}
                        {parseInfo.delimiter}-separated
                        {parseInfo.header
                          ? `, columns: ${parseInfo.columns.join("/")}`
                          : ", no header — using email/first/last/role order"}
                      </span>
                    )}
                  </span>
                )}
              </div>

              {parsedRows.length > 0 && (
                <div className="max-h-52 overflow-y-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-slate-100">
                      {parsedRows.map((r, i) => (
                        <tr key={i} className={r.valid ? "" : "bg-red-50"}>
                          <td className="px-2 py-1 text-slate-400 tabular-nums">
                            {r.line}
                          </td>
                          <td className="px-2 py-1 text-slate-700">
                            {r.email || "—"}
                          </td>
                          <td className="px-2 py-1 text-slate-500">
                            {[r.first, r.last].filter(Boolean).join(" ")}
                          </td>
                          <td className="px-2 py-1 text-slate-500">{r.role}</td>
                          <td className="px-2 py-1 text-right">
                            {!r.valid && (
                              <span className="text-red-600">
                                {r.reason || "unusable"}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <button
                onClick={runImport}
                disabled={importing || validRows.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {importing
                  ? "Importing…"
                  : `Import ${validRows.length} user${validRows.length === 1 ? "" : "s"}`}
              </button>
            </>
          )}

          {importResults?.error && (
            <p className="text-sm text-red-600">{importResults.error}</p>
          )}

          {importResults?.summary && (
            <>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded border border-green-200 bg-green-50 text-green-700">
                  {importResults.summary.created} created
                </span>
                <span className="px-2 py-1 rounded border border-slate-200 bg-slate-50 text-slate-600">
                  {importResults.summary.existing} already had accounts
                </span>
                {importResults.summary.error > 0 && (
                  <span className="px-2 py-1 rounded border border-red-200 bg-red-50 text-red-700">
                    {importResults.summary.error} failed
                  </span>
                )}
                <span className="px-2 py-1 rounded border border-slate-200 bg-slate-50 text-slate-500">
                  access: {(importResults.summary.services || []).join(", ")}
                </span>
              </div>

              <div className="max-h-52 overflow-y-auto border border-slate-200 rounded-lg">
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-slate-100">
                    {importResults.results.map((r, i) => (
                      <tr key={i}>
                        <td className="px-2 py-1 text-slate-700">{r.email}</td>
                        <td className="px-2 py-1">
                          <span
                            className={`px-1.5 py-0.5 rounded border ${IMPORT_STATUS_STYLE[r.status]}`}
                          >
                            {r.status}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-slate-400">
                          {r.detail || ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!sendSummary && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={sendInvites}
                    disabled={sending || importedIds.length === 0}
                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Send size={14} />
                    {sending
                      ? "Sending…"
                      : `Send set-password links to ${importedIds.length}`}
                  </button>
                  <span className="text-xs text-slate-400">
                    Nothing has been emailed yet.
                  </span>
                </div>
              )}

              {sendSummary?.error && (
                <p className="text-sm text-red-600 flex items-center gap-1.5">
                  <AlertCircle size={14} />
                  {sendSummary.error}
                </p>
              )}

              {sendSummary?.stopped_early && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  Sending stopped early after repeated failures
                  {sendSummary.not_attempted
                    ? ` — ${sendSummary.not_attempted} not attempted`
                    : ""}
                  . The most common cause is the mail provider's hourly sending
                  limit; the per-user send button in the list below will report
                  the exact reason, and the remainder can go out once the limit
                  resets.
                </p>
              )}

              {sendSummary && !sendSummary.error && (
                <p className="text-sm text-green-700 flex items-center gap-1.5">
                  <Check size={15} />
                  Sent {sendSummary.sent}
                  {sendSummary.failed > 0 && ` · ${sendSummary.failed} failed`}
                  {" — links expire in "}
                  {sendSummary.expires_in_days} days.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {pendingUsers.length > 0 && (
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
          <h3 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-bold">
              {pendingUsers.length}
            </span>
            Pending Registrations
          </h3>
          <div className="space-y-2">
            {pendingUsers.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between bg-white rounded border border-amber-100 px-3 py-2"
              >
                <div>
                  <p className="text-sm font-medium text-slate-800">
                    {u.first_name} {u.last_name}{" "}
                    <span className="text-slate-400 font-normal">
                      &lt;{u.email}&gt;
                    </span>
                  </p>
                  <p className="text-xs text-slate-400">
                    Requested{" "}
                    {u.requested_at
                      ? new Date(u.requested_at).toLocaleDateString()
                      : "—"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => approveUser(u.id)}
                    className="px-3 py-1 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => rejectUser(u.id, u.email)}
                    className="px-3 py-1 text-xs font-medium bg-red-100 text-red-700 rounded hover:bg-red-200"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">
            Users &amp; Roles
          </h3>
          <p className="text-xs text-slate-500">
            {users.length} user{users.length !== 1 ? "s" : ""} in{" "}
            {facility.facility_name}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchUsers}
            className="p-1.5 text-slate-500 hover:text-slate-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => {
              setShowImport(!showImport);
              setShowInvite(false);
            }}
            className="flex items-center px-3 py-1.5 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            Import Roster
          </button>
          <button
            onClick={() => setShowInvite(!showInvite)}
            className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            <UserPlus className="w-4 h-4 mr-1.5" />
            Invite User
          </button>
        </div>
      </div>

      {showInvite && (
        <form
          onSubmit={handleInvite}
          className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 space-y-3"
        >
          <h4 className="text-sm font-semibold text-slate-700">
            Invite New User
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <input
              value={invite.first_name}
              onChange={(e) =>
                setInvite({ ...invite, first_name: e.target.value })
              }
              placeholder="First name"
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            <input
              value={invite.last_name}
              onChange={(e) =>
                setInvite({ ...invite, last_name: e.target.value })
              }
              placeholder="Last name"
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
          </div>
          <input
            type="email"
            required
            value={invite.email}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })}
            placeholder="Email address"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
          <input
            type="password"
            required
            minLength={8}
            value={invite.password}
            onChange={(e) => setInvite({ ...invite, password: e.target.value })}
            placeholder="Temporary password (min 8 chars)"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
          <div className="flex items-center space-x-3">
            <select
              value={invite.role}
              onChange={(e) => setInvite({ ...invite, role: e.target.value })}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              <option value="viewer">Viewer</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <button
              type="submit"
              disabled={inviting}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {inviting ? "Inviting..." : "Send Invite"}
            </button>
            <button
              type="button"
              onClick={() => setShowInvite(false)}
              className="px-4 py-2 text-slate-600 text-sm rounded-lg hover:bg-slate-100"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email"
          className="flex-1 min-w-[200px] px-3 py-2 border border-slate-300 rounded-lg text-sm"
        />
        <select
          value={inviteFilter}
          onChange={(e) => setInviteFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        >
          <option value="all">All users</option>
          <option value="not_sent">No link sent yet</option>
          <option value="sent">Link sent</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
          title="Access to this facility. 'Account disabled' is the separate platform-wide switch, which leaves facility access in place."
        >
          <option value="all">Any status</option>
          <option value="active">Active</option>
          <option value="active_signed_in">Active and signed in now</option>
          <option value="disabled">No access (disabled)</option>
          <option value="account_disabled">
            Account disabled platform-wide
          </option>
        </select>
        <select
          // Same derived value the filter uses, so the control can never show
          // one thing while the list is filtered by another.
          value={effectiveSignIn}
          onChange={(e) => setSignInFilter(e.target.value)}
          disabled={statusFilter === "active_signed_in"}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm disabled:bg-slate-100 disabled:text-slate-400"
          title={
            statusFilter === "active_signed_in"
              ? 'Implied by "Active and signed in now" — anyone holding a live session has signed in at least once.'
              : "Anyone who has signed in already has a working password. Narrow to those who never have to find who is still waiting on a link, or to those who have to see who is actually using it."
          }
        >
          <option value="all">Any sign-in</option>
          <option value="never">Never signed in</option>
          <option value="signed_in">Has signed in</option>
        </select>
        <span className="text-xs text-slate-500">
          {visibleUsers.length} of {users.length}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-slate-500">
        <span>Invites link to</span>
        <select
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          className="px-2 py-1 border border-slate-300 rounded text-xs text-slate-700"
          title="Where users land after setting their password. Pick the app they will actually use."
        >
          {INVITE_DESTINATIONS.map((d) => (
            <option key={d.id} value={d.id}>
              {d.label}
            </option>
          ))}
        </select>
        <span className="text-slate-400">
          — field staff should get FieldIQ, not the portal
        </span>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm text-blue-900">
            {selectedVisible.length} selected
            {selectedVisible.length !== selected.size && (
              <span className="text-blue-700">
                {" "}
                ({selected.size - selectedVisible.length} hidden by the filter)
              </span>
            )}
          </span>
          <button
            onClick={sendSelected}
            disabled={bulkSending || selectedVisible.length === 0}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            <Send size={14} />
            {bulkSending ? "Sending…" : "Send set-password links"}
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            Clear selection
          </button>
        </div>
      )}

      {bulkResult && (
        <div className="mb-3 p-3 rounded-lg border text-sm bg-slate-50 border-slate-200">
          {bulkResult.error ? (
            <span className="text-red-600">{bulkResult.error}</span>
          ) : (
            <>
              <span className="text-slate-700">
                Sent {bulkResult.sent}
                {bulkResult.failed ? ` · ${bulkResult.failed} failed` : ""}
              </span>
              {bulkResult.stopped_early && (
                <p className="text-amber-700 mt-1">
                  Stopped early after repeated failures — usually the mail
                  provider's hourly limit. The ones that failed are still
                  selected, so you can retry once it resets.
                </p>
              )}
              {(bulkResult.results || [])
                .filter((r) => !r.sent && r.detail)
                .slice(0, 3)
                .map((r, i) => (
                  <p key={i} className="text-xs text-slate-500 mt-1">
                    {r.email || r.user_id}: {r.detail}
                  </p>
                ))}
            </>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-2.5 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  className="rounded"
                  title="Select all shown"
                />
              </th>
              <SortHeader field="name" label="User" />
              <SortHeader field="role" label="Role" />
              <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">
                Services
              </th>
              <SortHeader field="status" label="Status" />
              <SortHeader field="last_login" label="Last login" />
              <SortHeader field="invite" label="Set-password link" />
              <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-8 text-center text-slate-400"
                >
                  Loading...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-8 text-center text-slate-400"
                >
                  No users yet. Invite someone to get started.
                </td>
              </tr>
            ) : pagedUsers.length === 0 ? (
              // Previously unreachable: the old branch tested users.length
              // inside a users.length === 0 arm, so a filter matching nobody
              // showed an empty table with no explanation.
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-8 text-center text-slate-400"
                >
                  No users match this search or filter.
                </td>
              </tr>
            ) : (
              pagedUsers.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggleSelected(u.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-slate-900">
                      {u.first_name} {u.last_name}
                    </div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={u.client_role || "user"}
                      onChange={(e) => setRole(u.id, e.target.value)}
                      className="px-2 py-1 border border-slate-300 rounded text-xs"
                    >
                      <option value="viewer">Viewer</option>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(u.services || []).map((s) => (
                        <span
                          key={s.service_type}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
                        >
                          {s.service_type} ({s.access_role})
                          <button
                            onClick={() =>
                              setServiceAccess(
                                u.id,
                                s.service_type,
                                s.access_role,
                                true,
                              )
                            }
                            className="ml-1 text-blue-400 hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      <div className="relative group">
                        <button className="px-1.5 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500 hover:bg-slate-200">
                          <Plus className="w-3 h-3" />
                        </button>
                        <div className="absolute left-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 hidden group-hover:block z-10 space-y-1 min-w-[140px]">
                          {[
                            "rag",
                            "slm",
                            "private_llm",
                            "datamap",
                            "podcast",
                          ].map((svc) => (
                            <button
                              key={svc}
                              onClick={() =>
                                setServiceAccess(u.id, svc, "user")
                              }
                              className="block w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100 rounded"
                            >
                              {svc.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* Two different flags used to share this cell. The badge now
                      reports access to THIS facility — what the toggle beside
                      it changes — and the account-wide flag is a second line
                      only when it disagrees. Previously disabling a user and
                      filtering to "Disabled" found nothing, because the filter
                      read the account flag the toggle never touches. */}
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.access_is_active === false
                          ? "bg-amber-100 text-amber-700"
                          : u.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                      }`}
                      title={
                        u.access_is_active === false
                          ? "No access to this facility. The record is kept — use the toggle to restore it."
                          : u.is_active
                            ? ""
                            : "Account switched off platform-wide; facility access is still in place."
                      }
                    >
                      {u.access_is_active === false
                        ? "No access"
                        : u.is_active
                          ? "Active"
                          : "Account disabled"}
                    </span>
                  </td>
                  {/* Carries the never-signed-in warning that used to sit under
                      Status — same signal, no longer said twice in adjacent
                      columns. */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {/* Without this the "signed in now" filter produces a list
                        an admin cannot check — no row says why it is there. */}
                    {u.signed_in_now && (
                      <span
                        className="flex items-center gap-1 text-xs font-medium text-emerald-600"
                        title={
                          u.session_expires_at
                            ? `Session runs until ${new Date(u.session_expires_at).toLocaleTimeString()}. Means signed in and not logged out — not that the app is open.`
                            : undefined
                        }
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Signed in
                      </span>
                    )}
                    {u.last_login_at ? (
                      <span
                        className={`text-xs text-slate-600 ${u.signed_in_now ? "block text-slate-400" : ""}`}
                        title={new Date(u.last_login_at).toLocaleString()}
                      >
                        {new Date(u.last_login_at).toLocaleDateString()}
                      </span>
                    ) : (
                      <span
                        className="text-xs text-amber-600"
                        title="This user has never signed in."
                      >
                        Never
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.invite_sent_at ? (
                      <span
                        className="text-xs text-green-700"
                        title={`Last emailed ${new Date(u.invite_sent_at).toLocaleString()}`}
                      >
                        Sent {new Date(u.invite_sent_at).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-xs text-red-600">Never sent</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => runDiagnostics(u)}
                      className="p-1 rounded text-slate-400 hover:text-violet-600 hover:bg-violet-50"
                      title="Check why this user may not be able to sign in"
                    >
                      <Stethoscope className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => sendSetPasswordLink(u.id, u.email)}
                      disabled={sendingTo === u.id}
                      className="p-1 rounded text-blue-500 hover:bg-blue-50 disabled:opacity-40"
                      title="Email this user a set-password link"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openTempPassword(u)}
                      className="p-1 rounded text-slate-400 hover:text-amber-600 hover:bg-amber-50"
                      title="Set a temporary password to read out — use when email cannot reach them"
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button
                      // Reads access to THIS facility, which is what the
                      // endpoint changes — not the account-wide flag.
                      onClick={() =>
                        toggleUser(u.id, u.access_is_active !== false)
                      }
                      className={`p-1 rounded ${u.access_is_active !== false ? "text-amber-500 hover:bg-amber-50" : "text-green-500 hover:bg-green-50"}`}
                      title={
                        u.access_is_active !== false
                          ? "Disable — removes access and signs them out. Reversible; the record is kept."
                          : "Re-enable access to this facility"
                      }
                    >
                      {u.access_is_active !== false ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                    {/* Separate from the toggle on purpose. Disable and delete
                        were reachable through wording that did not tell them
                        apart, and a membership was lost that way. */}
                    <button
                      onClick={() => setConfirmDeleteUser(u)}
                      className="p-1 rounded text-slate-300 hover:text-rose-600 hover:bg-rose-50"
                      title="Delete this user's access record permanently"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {sortedUsers.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 bg-slate-50 text-xs text-slate-600">
            <span>
              {pageSize ? (
                <>
                  Showing {(currentPage - 1) * pageSize + 1}–
                  {Math.min(currentPage * pageSize, sortedUsers.length)} of{" "}
                  {sortedUsers.length}
                </>
              ) : (
                <>Showing all {sortedUsers.length}</>
              )}
            </span>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5">
                Per page
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="px-2 py-1 border border-slate-300 rounded text-xs"
                >
                  {[25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                  <option value={0}>All</option>
                </select>
              </label>
              {pageSize > 0 && totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-white"
                    title="Previous page"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span className="px-1">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-white"
                    title="Next page"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {tempPwFor && (
        <TempPasswordModal
          user={tempPwFor}
          password={tempPw}
          onPasswordChange={setTempPw}
          onRegenerate={() => setTempPw(generateTempPassword())}
          saving={tempPwSaving}
          msg={tempPwMsg}
          onSubmit={submitTempPassword}
          onClose={() => setTempPwFor(null)}
        />
      )}

      {diagFor && (
        <UserDiagnosticsModal
          user={diagFor}
          data={diag}
          loading={diagLoading}
          error={diagError}
          sending={sendingTo === diagFor.id}
          onSendLink={() => sendSetPasswordLink(diagFor.id, diagFor.email)}
          onClose={() => setDiagFor(null)}
        />
      )}

      {/* Names what is destroyed and offers Disable as the way out. The
          previous confirm for a destructive action said only "Revoke this
          user's service access?", which is why one was lost. */}
      {confirmDeleteUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">
                  Delete access record?
                </h3>
                <p className="text-sm text-slate-500">This cannot be undone.</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm text-slate-700 space-y-1">
              <p className="font-medium">
                {[confirmDeleteUser.first_name, confirmDeleteUser.last_name]
                  .filter(Boolean)
                  .join(" ") || confirmDeleteUser.email}
              </p>
              <p className="text-xs text-slate-500">
                {confirmDeleteUser.email} · {fid}
              </p>
            </div>
            <p className="text-xs text-slate-500">
              Erases their role and access history for this facility. The
              account itself stays, so they will still appear in the platform
              user list with no way to reach {fid} until access is granted again
              from scratch.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDeleteUser(null)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              {confirmDeleteUser.access_is_active !== false && (
                <button
                  onClick={() => {
                    const u = confirmDeleteUser;
                    setConfirmDeleteUser(null);
                    toggleUser(u.id, true);
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
                >
                  Disable instead
                </button>
              )}
              <button
                onClick={deleteUserAccess}
                disabled={deletingUser}
                className="flex-1 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {deletingUser ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TempPasswordModal = ({
  user,
  password,
  onPasswordChange,
  onRegenerate,
  saving,
  msg,
  onSubmit,
  onClose,
}) => {
  const done = msg && !msg.err;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-800">
              Set a temporary password
            </h3>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Emailing a link is the better route whenever mail works: it never
              puts a live credential in a chat log or a phone call. */}
          <p className="text-xs text-slate-500">
            For people email cannot reach. If their address works, send a
            set-password link instead.
          </p>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Password
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={password}
                onChange={(e) => onPasswordChange(e.target.value)}
                disabled={done}
                spellCheck={false}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono disabled:bg-slate-50"
              />
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(password)}
                title="Copy"
                className="p-2 text-slate-400 hover:text-slate-600 rounded"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {!done && (
              <button
                type="button"
                onClick={onRegenerate}
                className="mt-1.5 text-xs text-blue-600 hover:text-blue-700"
              >
                Generate another
              </button>
            )}
          </div>

          <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 space-y-1">
              <p>
                This signs them out everywhere and voids any set-password link
                already sent.
              </p>
              {/* Nothing forces a change at next sign-in — there is no
                  must_change_password flag — so the ask has to be verbal. */}
              <p>
                Nothing forces them to change it, so tell them to replace it
                from their profile.
              </p>
            </div>
          </div>

          {msg && (
            <p
              className={`text-sm ${msg.err ? "text-red-600" : "text-emerald-600"}`}
            >
              {msg.text}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200">
          {done ? (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 text-white text-sm rounded-lg hover:bg-slate-700"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={onSubmit}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <KeyRound className="w-4 h-4" />
                )}
                Set password
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const DIAG_SEVERITY = {
  blocker: {
    Icon: AlertCircle,
    cls: "text-red-600",
    row: "bg-red-50 border-red-200",
  },
  warning: {
    Icon: AlertTriangle,
    cls: "text-amber-600",
    row: "bg-amber-50 border-amber-200",
  },
  info: { Icon: Info, cls: "text-blue-600", row: "bg-blue-50 border-blue-200" },
  ok: {
    Icon: CheckCircle,
    cls: "text-emerald-600",
    row: "bg-emerald-50 border-emerald-200",
  },
};

const fmtWhen = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Never";

const UserDiagnosticsModal = ({
  user,
  data,
  loading,
  error,
  sending,
  onSendLink,
  onClose,
}) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
      <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
        <div className="min-w-0">
          <h3 className="font-semibold text-slate-800">Access check</h3>
          <p className="text-xs text-slate-500 truncate">{user.email}</p>
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-5 space-y-4 overflow-y-auto flex-1">
        {loading && (
          <p className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking…
          </p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {data && (
          <>
            <ul className="space-y-2">
              {data.findings.map((f, i) => {
                const s = DIAG_SEVERITY[f.severity] || DIAG_SEVERITY.info;
                return (
                  <li
                    key={i}
                    className={`flex gap-2 p-2.5 rounded-lg border ${s.row}`}
                  >
                    <s.Icon
                      className={`w-4 h-4 flex-shrink-0 mt-0.5 ${s.cls}`}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800">
                        {f.title}
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        {f.detail}
                      </p>
                      {f.action && (
                        <p className="text-xs text-slate-500 mt-1">
                          → {f.action}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs border-t border-slate-100 pt-3">
              {[
                ["Last signed in", fmtWhen(data.user.last_login_at)],
                ["Account created", fmtWhen(data.user.created_at)],
                ["Link last emailed", fmtWhen(data.invite.last_sent)],
                ["Live set-password links", data.links.live],
                ["Active sessions", data.sessions.live],
                [
                  "Facility access",
                  data.access.length
                    ? data.access
                        .map(
                          (a) =>
                            `${a.service_type}${a.is_active ? "" : " (off)"}`,
                        )
                        .join(", ")
                    : "None",
                ],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-slate-400">{k}</dt>
                  <dd className="text-slate-700">{v}</dd>
                </div>
              ))}
            </dl>

            {/* Says what this did NOT look at, so an all-clear is not read as
                "nothing is wrong with their account" in a wider sense. */}
            <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">
              Covers sign-in and access only. Chatbot and field-app activity is
              recorded per session, not per user, so it cannot be checked here.
            </p>
          </>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200">
        <button
          onClick={onSendLink}
          disabled={sending}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
        >
          {sending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          Send set-password link
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

// ── RAG Chat Panel (proxied through auth to avoid CORS) ────���────
